import { z } from "zod"
import { marketplaceIdSchema } from "../marketplace-id"
import type { ApiError } from "../result"
import type { GapReason } from "../verdict-reasons"
import { listingRunSchema, type ProductValueApplicationReport } from "./runs"
import { copyPreparationEvidenceSchema } from "./copy"

// ── Marketplace metadata ───────────────────────────────────────────────────

export const requirementLevelSchema = z.enum(["required", "recommended", "optional", "not_needed"])
export type RequirementLevel = z.infer<typeof requirementLevelSchema>

export const setSkuCategoryBodySchema = z.object({
  category: z.string().min(1),
})
export type SetSkuCategoryBody = z.infer<typeof setSkuCategoryBodySchema>

export const writeSkuAttributesBodySchema = z.object({
  /** Reviewed copy source and ownership snapshot, rechecked by the write owner. */
  copyPrecondition: copyPreparationEvidenceSchema.optional(),
  /** Config-scoped operator/LLM overrides keyed by attrCode. */
  attributes: z.record(z.string(), z.string()).optional(),
  /** Per-simple overrides keyed by simpleSku → attrCode → value. */
  simpleAttributes: z.record(z.string(), z.record(z.string(), z.string())).optional(),
  /** Reviewed omissions for non-required attributes that should not be emitted. */
  omittedAttributes: z
    .object({
      config: z.array(z.string().min(1)).optional(),
      simples: z.record(z.string(), z.array(z.string().min(1))).optional(),
    })
    .optional(),
  /** Source label for the override values (defaults to "human"). */
  source: z.enum(["ai", "human"]).optional(),
  /** When true, merge these overrides onto the SKU's existing stored overrides
   *  (incoming wins) instead of replacing the whole set. Lets a partial writer
   *  (e.g. copy) update its own attributes without clobbering overrides owned
   *  by another writer. Defaults to false (replace). */
  mergeWithExisting: z.boolean().optional(),
})
export type WriteSkuAttributesBody = z.infer<typeof writeSkuAttributesBodySchema>

/** One SKU's entry in a bulk attribute write — the single-SKU body plus the
 *  `configSku` it targets (the bulk endpoint takes the marketplace from the
 *  path and an array of these in the body). */
export const bulkWriteSkuAttributesItemSchema = writeSkuAttributesBodySchema.extend({
  configSku: z.string().min(1),
})
export type BulkWriteSkuAttributesItem = z.infer<typeof bulkWriteSkuAttributesItemSchema>

/** Body shared by the bulk write and bulk validation endpoints. The route
 *  selects mutation capability; callers cannot turn a write endpoint into a
 *  preview with an optional flag. Capped so one call stays bounded. */
export const bulkWriteSkuAttributesBodySchema = z
  .object({
    items: z.array(bulkWriteSkuAttributesItemSchema).min(1).max(500),
  })
  .strict()
export type BulkWriteSkuAttributesBody = z.infer<typeof bulkWriteSkuAttributesBodySchema>

/** Per-SKU outcome of a bulk attribute write (partial-tolerant). Successful
 *  outcomes carry only declaration identity, warnings, and application state;
 *  resolved attributes/long descriptions/tables would waste payload for a
 *  200-SKU chunk. `failed` carries the same error envelope the per-SKU endpoint
 *  would have returned. */
export const BULK_WRITE_SKU_OUTCOME_STATUS = {
  wouldWrite: "would_write",
  written: "written",
  failed: "failed",
} as const

export type BulkWriteSkuGap = {
  attrCode: string
  simpleSku: string | null
  reason: GapReason
  message: string
}

export type BulkWriteSkuValidationError = {
  attrCode: string
  simpleSku: string | null
  message: string
}

/** Write-time diagnostics are not an activation, feed, or import approval. */
export const COPY_PUBLICATION_STATUS = {
  blocked: "blocked",
  requiresValidation: "requires_validation",
} as const

export type CopyPublicationAssessment = {
  status: (typeof COPY_PUBLICATION_STATUS)[keyof typeof COPY_PUBLICATION_STATUS]
  gaps: BulkWriteSkuGap[]
  validationErrors: BulkWriteSkuValidationError[]
}

type BulkWriteSkuSuccess = {
  configSku: string
  writerRulesetHash: string
  gaps: BulkWriteSkuGap[]
  validationErrors: BulkWriteSkuValidationError[]
  publication?: CopyPublicationAssessment
}

type BulkWriteSkuFailure = {
  status: typeof BULK_WRITE_SKU_OUTCOME_STATUS.failed
  configSku: string
  error: ApiError
}

export type BulkValidateSkuOutcome =
  | (BulkWriteSkuSuccess & { status: typeof BULK_WRITE_SKU_OUTCOME_STATUS.wouldWrite })
  | BulkWriteSkuFailure
export type BulkValidateSkuAttributesResponse = { outcomes: BulkValidateSkuOutcome[] }

export type BulkWriteSkuOutcome =
  | (BulkWriteSkuSuccess & {
      status: typeof BULK_WRITE_SKU_OUTCOME_STATUS.written
      application: Pick<ProductValueApplicationReport, "eventId" | "status">
    })
  | BulkWriteSkuFailure
export type BulkWriteSkuAttributesResponse = { outcomes: BulkWriteSkuOutcome[] }

export const attributeOverrideSourceSchema = z.enum(["ai", "human", "system"])
export type AttributeOverrideSource = z.infer<typeof attributeOverrideSourceSchema>

export const attributeOverrideScopeSchema = z.enum(["config", "simple", "all"])
export type AttributeOverrideScope = z.infer<typeof attributeOverrideScopeSchema>

/** Destructive run-scoped cleanup of one exact override attribute. Preview is
 *  the default. A commit must echo the preview's count, so a changed match set
 *  fails closed instead of deleting a wider or stale selection. */
export const clearRunAttributeOverridesBodySchema = z
  .object({
    attrCode: z.string().trim().min(1).max(255),
    sources: z
      .array(attributeOverrideSourceSchema)
      .min(1)
      .max(3)
      .refine((sources) => new Set(sources).size === sources.length, {
        message: "sources must not contain duplicates",
      }),
    scope: attributeOverrideScopeSchema,
    commit: z.boolean(),
    expectedCount: z.number().int().nonnegative().optional(),
  })
  .strict()
  .superRefine((body, ctx) => {
    if (body.commit && body.expectedCount === undefined) {
      ctx.addIssue({
        code: "custom",
        path: ["expectedCount"],
        message: "expectedCount is required when commit is true",
      })
    }
    if (!body.commit && body.expectedCount !== undefined) {
      ctx.addIssue({
        code: "custom",
        path: ["expectedCount"],
        message: "expectedCount is only accepted when commit is true",
      })
    }
  })
export type ClearRunAttributeOverridesBody = z.infer<typeof clearRunAttributeOverridesBodySchema>

export const buildFeedBodySchema = z.object({
  marketplace: marketplaceIdSchema,
  runId: z.number().int().positive().optional(),
  configSkus: z.array(z.string().min(1)).optional(),
  familyMappingCsv: z.string().max(65_536).optional(),
})
export type BuildFeedBody = z.infer<typeof buildFeedBodySchema>

export const previewFamilyMappingBodySchema = z
  .object({
    marketplace: marketplaceIdSchema,
    runId: z.number().int().positive(),
    familyMappingCsv: z.string().max(65_536),
  })
  .strict()
export type PreviewFamilyMappingBody = z.infer<typeof previewFamilyMappingBodySchema>

export const COMPLETE_FEED_BUILD_MAX_PRODUCTS = 6_000

export const COMPLETE_FEED_BUILD_PURPOSE = {
  marketplaceListing: "marketplace_listing",
  rollbackSnapshot: "rollback_snapshot",
} as const
export const completeFeedBuildPurposeSchema = z.enum(COMPLETE_FEED_BUILD_PURPOSE)
export type CompleteFeedBuildPurpose = z.infer<typeof completeFeedBuildPurposeSchema>

export const completeFeedBuildBodySchema = z
  .object({
    marketplace: marketplaceIdSchema,
    purpose: completeFeedBuildPurposeSchema,
  })
  .strict()
export type CompleteFeedBuildBody = z.infer<typeof completeFeedBuildBodySchema>

// ── Approval (C-9) ─────────────────────────────────────────────────────────

export const approveRunFeedBodySchema = z
  .object({
    note: z.string().optional(),
    submissionHash: z.string().length(64).optional(),
  })
  .strict()
export type ApproveRunFeedBody = z.infer<typeof approveRunFeedBodySchema>

// ── Preconditions (C-8) ────────────────────────────────────────────────────

export const runPreconditionBlockerSchema = z.enum([
  "no_category",
  "no_writer",
  "no_catalog_row",
  "no_facts",
])
export type RunPreconditionBlocker = z.infer<typeof runPreconditionBlockerSchema>

export const runPreconditionRowSchema = z.object({
  configSku: z.string(),
  blockers: z.array(runPreconditionBlockerSchema),
})
export type RunPreconditionRow = z.infer<typeof runPreconditionRowSchema>

export const runPreconditionsResponseSchema = z.object({
  run: listingRunSchema,
  rows: z.array(runPreconditionRowSchema),
})
export type RunPreconditionsResponse = z.infer<typeof runPreconditionsResponseSchema>
